import { useEffect, useRef } from "react";

type HeatmapTuple = [number, number, number | null];

type HeatmapTooltipContext = {
  series: {
    yAxis: { categories: string[] };
    xAxis: { categories: string[] };
  };
  point: { x: number; y: number; value: number | null };
};

type HeatmapDataLabelContext = {
  point: { value: number | null };
};

interface HighchartsLike {
  chart: (renderTo: HTMLElement, options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    Highcharts?: HighchartsLike;
  }
}

export const CollaborationInsights = () => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current || typeof window === 'undefined') {
      return;
    }

    const highcharts = window.Highcharts;
    if (!highcharts) {
      return;
    }

    const heatmapData: HeatmapTuple[] = [
      [0, 0, null], [0, 1, 5], [0, 2, 22], [0, 3, 12], [0, 4, 8],
      [1, 0, 7], [1, 1, null], [1, 2, 15], [1, 3, 2], [1, 4, 3],
      [2, 0, 18], [2, 1, 11], [2, 2, null], [2, 3, 9], [2, 4, 2],
      [3, 0, 10], [3, 1, 4], [3, 2, 6], [3, 3, null], [3, 4, 1],
      [4, 0, 6], [4, 1, 1], [4, 2, 3], [4, 3, 0], [4, 4, null],
    ];

    const tooltipFormatter = function (this: HeatmapTooltipContext) {
      const from = this.series.yAxis.categories[this.point.y] ?? 'Unknown';
      const to = this.series.xAxis.categories[this.point.x] ?? 'Unknown';
      const value = this.point.value ?? 0;
      return `<b>${from}</b> handed off to <br><b>${to}</b>: <b>${value}</b> times`;
    };

    const dataLabelFormatter = function (this: HeatmapDataLabelContext) {
      if (this.point.value !== null && this.point.value !== undefined) {
        return String(this.point.value);
      }
      return undefined;
    };

    const options: Record<string, unknown> = {
      chart: {
        type: 'heatmap',
        backgroundColor: 'transparent',
        style: { fontFamily: 'Inter, sans-serif' },
      },
      title: { text: null },
      xAxis: {
        categories: ['User 1', 'User 2', 'User 3', 'User 4', 'User 5'],
        labels: { style: { color: '#6b7270' } },
      },
      yAxis: {
        categories: ['User 5', 'User 4', 'User 3', 'User 2', 'User 1'],
        title: null,
        reversed: true,
        labels: { style: { color: '#6b7270' } },
      },
      colorAxis: {
        min: 0,
        minColor: '#ffffff',
        maxColor: '#1e4841',
      },
      legend: { enabled: false },
      tooltip: { formatter: tooltipFormatter },
      series: [{
        name: 'Handoffs',
        borderWidth: 1,
        borderColor: '#e4e6e5',
        data: heatmapData,
        dataLabels: {
          enabled: true,
          color: '#6b7270',
          style: { textOutline: 'none' },
          formatter: dataLabelFormatter,
        },
      }],
      credits: { enabled: false },
    };

      highcharts.chart(chartRef.current, options);
  }, []);

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-brand-primary">Collaboration</h3>
        <button className="h-7 px-2 py-1.5 bg-brand-secondary rounded-lg flex items-center gap-0.5 text-brand-primary text-xs">
          Handoffs <i className="fa-solid fa-caret-down ml-1"></i>
        </button>
      </div>
      <div ref={chartRef} className="h-[260px]"></div>
    </div>
  );
};
